import { ContentContainer } from "@twinkltech/curriculum-architect-component-library";
import {
    ApiErrorResponse,
    ApiResponse,
    UpdateSubjectStatusPayload,
    type Curriculum,
    type Subject,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { Button, Text } from "@twinkltech/web-ui";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { GenericError } from "@/components/GenericError/GenericError";
import { NewUnitCard } from "@/components/NewUnitCard/NewUnitCard";
import { SubjectPlanHeader } from "@/components/SubjectPlanHeader/SubjectPlanHeader";
import { ViewCustomUnitModal } from "@/components/ViewCustomUnitModal/ViewCustomUnitModal";

import { useSkipCells } from "@/hooks/useSkipCells/useSkipCells";

import { FormattedCellKey } from "@/pages/EditSubjectPlan/types/types";
import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import type {
    TableData,
    TableResource,
} from "@/states/unitTableReducer/unitTableReducer";

import { SubjectHelper } from "@/utils/SubjectHelper/SubjectHelper";

import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { Z_INDEX_ORDERS } from "../config/zIndexOrders.const";

import { captureAppError } from "@/utils/monitoring/sentry";
import { SUBJECT_PLAN_OVERVIEW_NS } from "./SubjectPlanOverview.i18n";


export const SubjectPlanOverview = ({
  curriculum,
  error,
  isLoading = false,
  onUpdateSubjectStatus,
  resources,
  subject,
  tableData,
}: {
  curriculum?: Curriculum | null;
  error?: ApiErrorResponse | null;
  isLoading?: boolean;
  onUpdateSubjectStatus: (params: {
    curriculumId: string;
    subjectId: string;
    payload: UpdateSubjectStatusPayload;
  }) => ApiResponse<Partial<Subject | void>>;
  resources: TableResource[];
  subject?: Subject;
  tableData: Pick<TableData, "columns" | "rows" | "cells">;
}) => {
  const { t } = useTranslation(SUBJECT_PLAN_OVERVIEW_NS);
  const { incrementEngagementCount } = useFeedback();

  // So we can manage a single modal instance instead of one per custom unit
  const [viewCustomUnitModalInfo, setViewCustomUnitModalInfo] = useState<{
    title: string;
    description: string;
    cohort: string;
    startOnColumn: string;
    endOnColumn: string;
  } | null>(null);

  // Define styles for the sticky header and first column
  const theadBorderStyles = [
    "sticky top-0 bg-white",
    Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.STICKY_COLUMN_HEADERS,
  ].join(" ");

  const tableRef = useRef<HTMLTableElement>(null);

  const firstColumnStyles = [
    "sticky left-0 bg-white overflow-hidden truncate w-[170px] font-normal",
    Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.STICKY_ROW_HEADERS,
  ].join(" ");

  const orderedColumns = [...tableData.columns].sort(
    (a, b) => a.order - b.order
  );

  const orderedRows = [...tableData.rows].sort((a, b) => a.order - b.order);

  // Use the skipCells hook to determine which cells to skip rendering
  const { skipSpannedCells } = useSkipCells(tableData.cells, orderedColumns);

  if (isLoading) {
    return <LoadingPage />;
  } else if (error || !curriculum || !subject) {
    captureAppError(error || new Error("Unknown error loading subject plan overview"), {
      action: "load",
      area: "subject-plan-overview",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <SubjectPlanHeader
        curriculum={curriculum}
        isLoading={isLoading}
        subject={subject}
        onUpdateSubjectStatus={onUpdateSubjectStatus}
      />

      {/* Info block */}
      <div className="max-w-[800px] mb-10">
        <Text as="h2" variant="xl-medium">
          {t("info-block.title", {
            subject: SubjectHelper.getLabel(subject),
          })}
        </Text>
        <Text as="p">{t("info-block.description")}</Text>
      </div>

      {/* Planner table and actions */}
      <div
        aria-label={t("planner-overview.label")}
        className="w-full flex flex-col gap-4 overflow-hidden pr-6 pb-8 grow-1"
        role="region"
      >
        {/* Planner table */}
        <div className="max-h-[calc(100vh-300px)] min-h-[300px] overflow-x-auto overflow-y-auto relative">
          <table
            ref={tableRef}
            className={[
              "border-spacing-3 border-separate relative",
              "**:[td,th]:whitespace-nowrap",
              "**:[th]:ellipsis **:[th]:overflow-hidden",
              "**:[th]:max-w-[170px] **:[th]:min-w-[170px] **:[th]:h-[84px]",
              "**:[td]:w-[170px]  **:[td]:h-[84px] **:[td]:bg-neutral-100 **:[td]:rounded-lg",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <thead className={theadBorderStyles}>
              <tr className="h-[72px]">
                <th
                  className={["py-150 px-300 text-left", firstColumnStyles]
                    .filter(Boolean)
                    .join(" ")}
                  role="columnheader"
                  scope="col"
                >
                  <span className="sr-only">Cohorts</span>
                </th>
                {orderedColumns.map((ci) => (
                  <th
                    key={ci.id}
                    className="py-150 px-300 text-left min-w-[170px] font-normal"
                    role="columnheader"
                    scope="col"
                  >
                    {ci.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedRows.map((row) => {
                return (
                  <tr key={row.id}>
                    <th
                      className={firstColumnStyles}
                      role="rowheader"
                      scope="row"
                    >
                      <div className="text-left py-150 px-300 truncate max-w-[170px]">
                        {row.title}
                      </div>
                    </th>
                    {orderedColumns.map((col) => {
                      const formattedCellKey: FormattedCellKey = `${row.id}__${col.id}`;

                      // Skip rendering this cell if it's covered by a previous cell's span
                      if (skipSpannedCells.has(formattedCellKey)) {
                        return null;
                      }

                      const targetCell = tableData.cells.find(
                        (res) => res.column === col.id && res.row === row.id
                      );

                      if (targetCell?.isCustom) {
                        // todo: add modal to view or edit custom unit

                        return (
                          <td
                            key={col.id}
                            aria-label={`${row.title} ${col.title}`}
                            className="max-w-[170px]"
                            colSpan={targetCell.span}
                          >
                            <div className="border rounded-[8px]">
                              <Button
                                hideMenuButton
                                as={NewUnitCard}
                                className="relative bg-white flex justify-between rounded-[8px] py-1 shadow-[0_1px_2px_0px_rgba(0,0,0,0.1)] gap-2 px-4 w-full hover:bg-gray-50 cursor-pointer"
                                resource={{
                                  id: targetCell.id,
                                  name: targetCell.title,
                                }}
                                variant="ghost"
                                onClick={() => {
                                  let endOnColumn = col.title;

                                  // if the custom unit has a start column, step through the ordered columns to find the end column based on the span, otherwise, return the start column
                                  if (targetCell.span) {
                                    const startIndex = orderedColumns.findIndex(
                                      (column) => column.id == col.id
                                    );
                                    if (startIndex !== -1) {
                                      const endIndex =
                                        startIndex + targetCell.span - 1;
                                      if (endIndex < orderedColumns.length) {
                                        endOnColumn =
                                          orderedColumns[endIndex].title;
                                      }
                                    }
                                  }

                                  setViewCustomUnitModalInfo({
                                    title: targetCell.title,
                                    description: targetCell.description || "",
                                    cohort: row.title,
                                    startOnColumn: col.title,
                                    endOnColumn,
                                  });
                                }}
                              />
                            </div>
                          </td>
                        );
                      }

                      const targetResource = targetCell
                        ? resources.find(
                            (res) => res.id === targetCell.resourceId
                          )
                        : null;

                      if (!targetCell || !targetResource) {
                        return (
                          <td
                            key={formattedCellKey}
                            aria-label={`${row.title} ${col.title}`}
                          >
                            <div className="w-full h-full flex justify-center items-center rounded-lg " />
                          </td>
                        );
                      }

                      return (
                        <td
                          key={col.id}
                          aria-label={`${row.title} ${col.title}`}
                          className="max-w-[170px]"
                          colSpan={targetCell.span}
                        >
                          <div className="relative">
                            <div
                              aria-label={t("table.card-label", {
                                unitTitle: targetResource.name,
                              })}
                              className="relative border rounded-lg"
                              role="group"
                            >
                              <Link
                                aria-label={targetResource.name}
                                to={generatePath(
                                  paths.SCHEME_LEARNING_SEQUENCE_PATH,
                                  {
                                    curriculum_id: curriculum.id,
                                    subject_id: subject.id,
                                    scheme_id: targetResource.id,
                                    cell_id: targetCell.id,
                                  }
                                )}
                                onClick={() => {
                                  incrementEngagementCount();
                                }}
                              >
                                <NewUnitCard
                                  hideMenuButton
                                  className="relative bg-white flex justify-between w-auto rounded-lg px-1 py-1 shadow-[0_1px_2px_0px_rgba(0,0,0,0.1)] gap-2 px-4 hover:bg-gray-50 cursor-pointer"
                                  resource={targetResource}
                                />
                              </Link>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {viewCustomUnitModalInfo !== null ? (
        <ViewCustomUnitModal
          open
          customUnit={viewCustomUnitModalInfo}
          onClose={() => setViewCustomUnitModalInfo(null)}
        />
      ) : null}
    </ContentContainer>
  );
};
